import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./ReduxStore_Store";

/**
 * DraftSelectionSlice
 *
 * Manages selected paragraph IDs for draft content editing.
 * This state is per-project and per-document (scoped by doc_id).
 * Enforces a maximum of 5 selected paragraphs per document.
 */

// Constants
export const MAX_PARAGRAPH_SELECTIONS = 5;

// Types
interface ToggleParagraphPayload {
  projectId: string;
  docId: string;
  paragraphId: string;
}

interface SelectParagraphsPayload {
  projectId: string;
  docId: string;
  paragraphIds: string[];
}

interface ClearDocumentPayload {
  projectId: string;
  docId: string;
}

interface ClearProjectPayload {
  projectId: string;
}

interface DocumentSelections {
  [docId: string]: string[];
}

interface ProjectSelections {
  [projectId: string]: DocumentSelections;
}

interface DraftSelectionState {
  selections: ProjectSelections;
}

const initialState: DraftSelectionState = {
  selections: {},
};

const draftSelectionSlice = createSlice({
  name: "draftSelectionSlice",
  initialState,
  reducers: {
    toggleParagraphSelection: (state, action: PayloadAction<ToggleParagraphPayload>) => {
      const { projectId, docId, paragraphId } = action.payload;

      if (!projectId || !docId || paragraphId === undefined) {
        console.warn('[toggleParagraphSelection] Missing required fields:', { projectId, docId, paragraphId });
        return;
      }

      // Initialize project selections if needed
      if (!state.selections[projectId]) {
        state.selections[projectId] = {};
      }

      // Initialize document selections if needed
      if (!state.selections[projectId][docId]) {
        state.selections[projectId][docId] = [];
      }

      const selectedIds = state.selections[projectId][docId];
      const index = selectedIds.indexOf(paragraphId);

      if (index >= 0) {
        // Already selected - remove it
        selectedIds.splice(index, 1);
      } else {
        // Not selected - check max limit before adding
        if (selectedIds.length >= MAX_PARAGRAPH_SELECTIONS) {
          console.warn(`[DraftSelection] Cannot select more than ${MAX_PARAGRAPH_SELECTIONS} paragraphs`);
          return;
        }
        selectedIds.push(paragraphId);
      }

      console.log(`[DraftSelection] Toggled paragraph ${paragraphId} for doc ${docId}. Now selected: ${selectedIds.length}`);
    },

    selectParagraphs: (state, action: PayloadAction<SelectParagraphsPayload>) => {
      const { projectId, docId, paragraphIds } = action.payload;

      if (!projectId || !docId || !Array.isArray(paragraphIds)) {
        console.warn('[selectParagraphs] Missing required fields:', { projectId, docId, paragraphIds });
        return;
      }

      // Initialize project selections if needed
      if (!state.selections[projectId]) {
        state.selections[projectId] = {};
      }

      // Set the selections (deduplicate and enforce max limit)
      const uniqueIds = [...new Set(paragraphIds)];
      const limitedIds = uniqueIds.slice(0, MAX_PARAGRAPH_SELECTIONS);

      if (uniqueIds.length > MAX_PARAGRAPH_SELECTIONS) {
        console.warn(`[DraftSelection] Truncated selection to ${MAX_PARAGRAPH_SELECTIONS} paragraphs`);
      }

      state.selections[projectId][docId] = limitedIds;

      console.log(`[DraftSelection] Selected ${limitedIds.length} paragraphs for doc ${docId}`);
    },

    clearDocumentSelections: (state, action: PayloadAction<ClearDocumentPayload>) => {
      const { projectId, docId } = action.payload;

      if (!projectId || !docId) {
        console.warn('[clearDocumentSelections] Missing required fields:', { projectId, docId });
        return;
      }

      if (state.selections[projectId]?.[docId]) {
        state.selections[projectId][docId] = [];
        console.log(`[DraftSelection] Cleared selections for doc ${docId}`);
      }
    },

    clearProjectSelections: (state, action: PayloadAction<ClearProjectPayload>) => {
      const { projectId } = action.payload;

      if (!projectId) {
        console.warn('[clearProjectSelections] Missing projectId');
        return;
      }

      if (state.selections[projectId]) {
        state.selections[projectId] = {};
        console.log(`[DraftSelection] Cleared all selections for project ${projectId}`);
      }
    },

    clearAllSelections: (state) => {
      state.selections = {};
      console.log(`[DraftSelection] Cleared all selections`);
    },
  },
});

export const {
  toggleParagraphSelection,
  selectParagraphs,
  clearDocumentSelections,
  clearProjectSelections,
  clearAllSelections,
} = draftSelectionSlice.actions;

// Selectors (curried pattern for use with useSelector)
export const selectParagraphIds = (projectId: string | undefined, docId: string | undefined) =>
  (state: RootState): string[] => {
    if (!projectId || !docId) return [];
    return state.draftSelectionSlice?.selections?.[projectId]?.[docId] || [];
  };

export const selectParagraphCount = (projectId: string | undefined, docId: string | undefined) =>
  (state: RootState): number => {
    if (!projectId || !docId) return 0;
    return state.draftSelectionSlice?.selections?.[projectId]?.[docId]?.length || 0;
  };

export const selectIsParagraphSelected = (
  projectId: string | undefined,
  docId: string | undefined,
  paragraphId: string | undefined
) => (state: RootState): boolean => {
    if (!projectId || !docId || paragraphId === undefined) return false;
    const selectedIds = state.draftSelectionSlice?.selections?.[projectId]?.[docId] || [];
    return selectedIds.includes(paragraphId);
  };

export const selectAllProjectSelections = (projectId: string | undefined) =>
  (state: RootState): DocumentSelections => {
    if (!projectId) return {};
    return state.draftSelectionSlice?.selections?.[projectId] || {};
  };

export const selectCanSelectMore = (projectId: string | undefined, docId: string | undefined) =>
  (state: RootState): boolean => {
    if (!projectId || !docId) return true;
    const count = state.draftSelectionSlice?.selections?.[projectId]?.[docId]?.length || 0;
    return count < MAX_PARAGRAPH_SELECTIONS;
  };

export default draftSelectionSlice.reducer;
